import { IonButton, IonIcon } from '@ionic/react';
import {
  arrowUpOutline,
  closeOutline,
  imagesOutline,
  stop,
} from 'ionicons/icons';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAgentTrackMentionSearch } from '../../hooks/useAgentTrackMentionSearch';
import {
  AGENT_PROMPT_SUGGESTIONS,
  AgentChatImageAttachment,
  AgentPromptSuggestion,
} from './AgentChat.types';
import AgentAttachedTrackCard from './AgentAttachedTrackCard';
import {
  AgentAttachedTrack,
  formatAttachedTrackSubmission,
  formatDraftSubmission,
} from './agentAttachedTrack';
import {
  AGENT_IMAGE_LIMIT,
  AGENT_SCREENSHOT_DEFAULT_PROMPT,
  attachmentSrc,
  attachmentsFromFiles,
  imageFilesFrom,
} from './agentImageAttachments';
import {
  clearComposerDraft,
  readComposerDraft,
  saveComposerDraft,
} from './agentComposerDrafts';
import {
  parseTrackMention,
  removeTrackMention,
  stripCompletedTrackTokens,
} from './agentTrackMention';
import { AgentTrackSearchResults } from './AgentTrackSearchResults';

/** Target for the dashboard shortcut that jumps straight to the question box. */
export const AGENT_COMPOSER_INPUT_ID = 'agent-composer-input';

export type AgentComposerHandle = {
  insertTrack: (track: AgentAttachedTrack) => void;
};

interface AgentChatComposerProps {
  /** Which conversation the draft belongs to; changing it swaps the draft. */
  draftKey: string;
  onSubmit: (prompt: string, images?: AgentChatImageAttachment[]) => void;
  onStop?: () => void;
  isRunning?: boolean;
  /** Suggestions are only worth showing before a conversation exists. */
  showSuggestions: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Optional source context badges rendered inside the input box. */
  sourcesBar?: React.ReactNode;
  /** Conversation stats, shown immediately left of the send button. */
  statsBar?: React.ReactNode;
  /** Compact tools on the left of the composer footer, e.g. automations. */
  toolsBar?: React.ReactNode;
  /** Dev-only model picker, shown left of usage stats. */
  modelPicker?: React.ReactNode;
}

export const AgentChatComposer = forwardRef<
  AgentComposerHandle,
  AgentChatComposerProps
>(function AgentChatComposer(
  {
    draftKey,
    onSubmit,
    onStop,
    isRunning = false,
    showSuggestions,
    disabled = false,
    placeholder = 'Ask anything about your library, crates or DJ software…',
    sourcesBar,
    statsBar,
    toolsBar,
    modelPicker,
  },
  ref,
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const draftRef = useRef('');
  const attachedTrackRef = useRef<AgentAttachedTrack | null>(null);
  /** After a track is attached, ignore @-mention parsing until the user types @. */
  const suppressMentionsRef = useRef(false);
  const [draft, setDraft] = useState('');
  const [caret, setCaret] = useState(0);
  const [mentionsEnabled, setMentionsEnabled] = useState(true);
  const [attachedTrack, setAttachedTrack] = useState<AgentAttachedTrack | null>(
    null,
  );
  const [images, setImages] = useState<AgentChatImageAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  attachedTrackRef.current = attachedTrack;
  const canSend =
    (draft.trim().length > 0 || Boolean(attachedTrack) || images.length > 0) &&
    !disabled &&
    !isRunning;

  const applyDraft = useCallback((next: string) => {
    draftRef.current = next;
    setDraft(next);
    const el = textareaRef.current;
    if (el && el.value !== next) {
      el.value = next;
    }
  }, []);

  const placeCaret = useCallback((index: number) => {
    const el = textareaRef.current;
    if (!el) {
      setCaret(index);
      return;
    }
    el.focus();
    el.setSelectionRange(index, index);
    setCaret(index);
  }, []);

  useEffect(() => {
    const restored = readComposerDraft(draftKey);
    const rawText = restored?.text ?? '';
    const tracks = restored?.tracks ?? [];
    const seed = tracks[0] ?? null;
    const text = seed ? stripCompletedTrackTokens(rawText) : rawText;

    suppressMentionsRef.current = Boolean(seed);
    setMentionsEnabled(!seed);
    setAttachedTrack(seed);
    // Screenshots are too heavy for the localStorage draft, so they belong to
    // the conversation being written and reset when the draft swaps.
    setImages([]);
    applyDraft(text);
    setCaret(text.length);

    return () => {
      saveComposerDraft(draftKey, {
        text: draftRef.current,
        tracks: attachedTrackRef.current ? [attachedTrackRef.current] : [],
      });
    };
  }, [applyDraft, draftKey]);

  const mention = useMemo(() => {
    if (disabled || !mentionsEnabled || suppressMentionsRef.current) {
      return null;
    }
    return parseTrackMention(draft, caret);
  }, [caret, disabled, draft, mentionsEnabled]);

  const { results, isSearching, activeIndex, setActiveIndex } =
    useAgentTrackMentionSearch(mention?.query ?? null);

  const lockMentionsAfterTrackAttach = useCallback(() => {
    suppressMentionsRef.current = true;
    setMentionsEnabled(false);
  }, []);

  const attachTrack = useCallback(
    (track: AgentAttachedTrack, nextDraft: string, caretAt: number) => {
      setAttachedTrack(track);
      lockMentionsAfterTrackAttach();
      applyDraft(nextDraft);
      placeCaret(caretAt);
    },
    [applyDraft, lockMentionsAfterTrackAttach, placeCaret],
  );

  const clearAttachedTrack = useCallback(() => {
    setAttachedTrack(null);
    suppressMentionsRef.current = false;
    setMentionsEnabled(true);
  }, []);

  const insertTrack = useCallback(
    (track: AgentAttachedTrack) => {
      const current = stripCompletedTrackTokens(draftRef.current);
      attachTrack(track, current, current.length);
    },
    [attachTrack],
  );

  useImperativeHandle(ref, () => ({ insertTrack }), [insertTrack]);

  const addImageFiles = useCallback(
    (files: File[]) => {
      if (disabled) return;
      const accepted = imageFilesFrom(files);
      if (!accepted.length) return;
      void (async () => {
        const room = AGENT_IMAGE_LIMIT - images.length;
        const added = await attachmentsFromFiles(accepted, room);
        if (!added.length) return;
        setImages((current) =>
          [...current, ...added].slice(0, AGENT_IMAGE_LIMIT),
        );
      })();
    },
    [disabled, images.length],
  );

  const removeImage = useCallback((id: string) => {
    setImages((current) => current.filter((image) => image.id !== id));
  }, []);

  const send = () => {
    if (!canSend) return;
    const text = attachedTrack
      ? formatAttachedTrackSubmission(draft, attachedTrack)
      : formatDraftSubmission(draft);
    const prompt =
      text ?? (images.length > 0 ? AGENT_SCREENSHOT_DEFAULT_PROMPT : null);
    if (!prompt) return;
    onSubmit(prompt, images.length > 0 ? images : undefined);
    suppressMentionsRef.current = false;
    setMentionsEnabled(true);
    setAttachedTrack(null);
    setImages([]);
    applyDraft('');
    setCaret(0);
    clearComposerDraft(draftKey);
  };

  const selectMentionTrack = useCallback(
    (track: AgentAttachedTrack) => {
      if (!mention) return;
      const withoutMention = removeTrackMention(draft, mention).trimStart();
      const cleaned = stripCompletedTrackTokens(withoutMention);
      attachTrack(track, cleaned, cleaned.length);
    },
    [attachTrack, draft, mention],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === '@') {
      suppressMentionsRef.current = false;
      setMentionsEnabled(true);
    }

    if (mention) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!results.length) return;
        setActiveIndex((index) => (index + 1) % results.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!results.length) return;
        setActiveIndex(
          (index) => (index - 1 + results.length) % results.length,
        );
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey && results.length > 0) {
        event.preventDefault();
        selectMentionTrack(results[activeIndex] ?? results[0]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        const next = removeTrackMention(draft, mention);
        applyDraft(next);
        placeCaret(mention.start);
        return;
      }
    }

    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    send();
  };

  const applySuggestion = (suggestion: AgentPromptSuggestion) => {
    applyDraft(suggestion.prompt);
    placeCaret(suggestion.prompt.length);
  };

  return (
    <div className="agent-composer">
      {showSuggestions && (
        <div className="agent-suggestions">
          {AGENT_PROMPT_SUGGESTIONS.map((suggestion) => (
            <button
              key={suggestion.label}
              type="button"
              className="agent-suggestion"
              onClick={() => applySuggestion(suggestion)}
            >
              {suggestion.label}
            </button>
          ))}
        </div>
      )}
      <div
        className={`agent-composer-box${isDragging ? ' is-dragging' : ''}`}
        onDragOver={(event) => {
          if (disabled || !event.dataTransfer.types.includes('Files')) return;
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
          }
          setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          if (disabled || !event.dataTransfer.files.length) return;
          addImageFiles(Array.from(event.dataTransfer.files));
        }}
      >
        {sourcesBar}
        {images.length > 0 && (
          <div className="agent-composer-images">
            {images.map((image, index) => (
              <div key={image.id} className="agent-composer-image">
                <img
                  src={attachmentSrc(image)}
                  alt={`Screenshot ${index + 1}`}
                />
                {!disabled && (
                  <button
                    type="button"
                    className="agent-composer-image-remove"
                    aria-label={`Remove screenshot ${index + 1}`}
                    onClick={() => removeImage(image.id)}
                  >
                    <IonIcon icon={closeOutline} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="agent-composer-input-area">
          <div className="agent-composer-text-wrap">
            {mention && (
              <AgentTrackSearchResults
                className="is-composer"
                results={results}
                activeIndex={activeIndex}
                isSearching={isSearching}
                query={mention.query}
                onActiveIndexChange={setActiveIndex}
                onSelect={selectMentionTrack}
              />
            )}
            {attachedTrack && (
              <AgentAttachedTrackCard
                track={attachedTrack}
                onRemove={disabled ? undefined : clearAttachedTrack}
              />
            )}
            <textarea
              ref={textareaRef}
              id={AGENT_COMPOSER_INPUT_ID}
              className="agent-composer-input"
              placeholder={
                attachedTrack
                  ? 'Ask about this track…'
                  : images.length > 0
                    ? 'Anything to add about these screenshots?'
                    : placeholder
              }
              rows={3}
              disabled={disabled}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              defaultValue=""
              onChange={(event) => {
                const next = event.target.value;
                if (next === draftRef.current) {
                  setCaret(event.target.selectionStart ?? next.length);
                  return;
                }
                draftRef.current = next;
                setDraft(next);
                setCaret(event.target.selectionStart ?? next.length);
              }}
              onSelect={(event) => {
                setCaret(event.currentTarget.selectionStart ?? draft.length);
              }}
              onKeyDown={handleKeyDown}
              onPaste={(event) => {
                const files = imageFilesFrom(event.clipboardData.files);
                if (!files.length) return;
                event.preventDefault();
                addImageFiles(files);
              }}
            />
          </div>
        </div>
        <div className="agent-composer-meta">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(event) => {
              addImageFiles(Array.from(event.target.files ?? []));
              event.target.value = '';
            }}
          />
          <button
            type="button"
            className="agent-composer-attach"
            title="Attach screenshots"
            aria-label="Attach screenshots"
            disabled={disabled || images.length >= AGENT_IMAGE_LIMIT}
            onClick={() => fileInputRef.current?.click()}
          >
            <IonIcon icon={imagesOutline} />
          </button>
          {toolsBar}
          {modelPicker}
          {statsBar}
          <IonButton
            className={`agent-composer-send${isRunning ? ' is-stop' : ''}`}
            size="small"
            shape="round"
            color={isRunning ? 'danger' : undefined}
            disabled={isRunning ? disabled : !canSend}
            onClick={() => {
              if (isRunning) {
                onStop?.();
                return;
              }
              send();
            }}
            aria-label={isRunning ? 'Stop agent run' : 'Start agent run'}
          >
            <IonIcon
              slot="icon-only"
              icon={isRunning ? stop : arrowUpOutline}
            />
          </IonButton>
        </div>
      </div>
    </div>
  );
});

export default AgentChatComposer;
